import { isAllowedUrl } from '@/lib/crm/utils/urlValidation';

export interface LocationValueLabelProps {
  value: string | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Display component for location values.
 * Renders location text as a clickable Google Maps search link.
 *
 * @example
 * <LocationValueLabel value="New York, NY" />
 * // Renders: <a href="https://www.google.com/maps/search/?api=1&query=New+York%2C+NY" ...>New York, NY</a>
 */
export const LocationValueLabel: React.FC<LocationValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (!value) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  // If the value is already a URL (e.g. Zoom/Teams link), link directly to it
  // instead of wrapping it in a Google Maps search
  const isUrl = isAllowedUrl(trimmedValue);
  const href = isUrl
    ? encodeURI(trimmedValue)
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmedValue)}`;
  const displayText = isUrl
    ? trimmedValue.replace(/^https?:\/\//, '').replace(/\/$/, '')
    : trimmedValue;

  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      onClick={e => e.stopPropagation()}
      className={`${sizeClass} ${truncateClasses} text-primary hover:underline break-all ${className}`}
      title={truncate ? trimmedValue : undefined}
      aria-label={`${trimmedValue} (opens in new tab)`}
    >
      {displayText}
    </a>
  );
};
